import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Calendar,
  Users,
  Play,
  Mail,
  Download,
  X,
  ChevronRight,
  ChevronLeft,
  Search,
  AlertTriangle,
  Video,
  Plus,
  ExternalLink,
  RefreshCw,
  Check,
  XCircle,
  Eye,
  Send,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  listWebinars,
  getWebinarRegistrants,
  sendReplayEmail,
  deleteScheduledMeeting,
  approveRegistrant,
  rejectRegistrant,
  resendConfirmationEmail,
} from "../../../services/roomServices";

// ── Status Badge ──────────────────────────────────────────────
function StatusBadge({ status }) {
  const styles = {
    Live: "bg-green-500/15 text-green-400 border-green-500/30",
    Scheduled: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    Ended: "bg-gray-500/15 text-gray-400 border-gray-500/30",
    Draft: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status] || styles.Scheduled}`}>
      {status === "Live" && <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-400 mr-1.5 animate-pulse" />}
      {status}
    </span>
  );
}

// ── Registrants Modal ─────────────────────────────────────────
function RegistrantsModal({ webinarId, webinarName, onClose }) {
  const [registrants, setRegistrants] = useState([]);
  const [approvalMode, setApprovalMode] = useState("auto");
  const [detailRegistrant, setDetailRegistrant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [actionLoading, setActionLoading] = useState(null);

  const fetchRegistrants = async () => {
    try {
      const res = await getWebinarRegistrants(webinarId);
      if (res.success) {
        setRegistrants(res.registrants || []);
        setApprovalMode(res.registrationApprovalMode || "auto");
      }
    } catch (e) {
      toast.error("Failed to load registrants");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegistrants();
  }, [webinarId]);

  const handleApprove = async (email) => {
    setActionLoading(email);
    try {
      const res = await approveRegistrant(webinarId, email);
      if (res.success) {
        toast.success("Registrant approved");
        setRegistrants((prev) => prev.map((r) => r.email === email ? { ...r, status: "approved" } : r));
      }
    } catch (e) {
      toast.error("Failed to approve registrant");
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (email) => {
    if (!window.confirm("Remove this registrant? This cannot be undone.")) return;
    setActionLoading(email);
    try {
      const res = await rejectRegistrant(webinarId, email);
      if (res.success) {
        toast.success("Registrant removed");
        setRegistrants((prev) => prev.filter((r) => r.email !== email));
      }
    } catch (e) {
      toast.error("Failed to remove registrant");
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = registrants.filter((r) => {
    const q = search.toLowerCase();
    return (
      (r.firstName || "").toLowerCase().includes(q) ||
      (r.lastName || "").toLowerCase().includes(q) ||
      (r.email || "").toLowerCase().includes(q)
    );
  });

  const pendingCount = registrants.filter((r) => r.status === "pending").length;

  const exportCSV = () => {
    // Collect all unique custom field keys across all registrants
    const customFieldKeys = [];
    const seen = new Set();
    registrants.forEach((r) => {
      if (r.customFields && typeof r.customFields === "object") {
        Object.keys(r.customFields).forEach((k) => {
          if (!seen.has(k)) {
            seen.add(k);
            customFieldKeys.push(k);
          }
        });
      }
    });
    const headers = ["#", "First Name", "Last Name", "Email", "Phone", "Registered At", "Type", "Status", ...customFieldKeys];
    const rows = registrants.map((r) => [
      r.index,
      r.firstName,
      r.lastName,
      r.email,
      r.phone || "",
      r.registeredAt ? new Date(r.registeredAt).toLocaleString() : "",
      r.type,
      r.status || "approved",
      ...customFieldKeys.map((k) => {
        const v = r.customFields?.[k];
        if (v == null) return "";
        return typeof v === "object" ? JSON.stringify(v) : String(v);
      }),
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(webinarName || "webinar").replace(/[^a-zA-Z0-9]/g, "_")}_registrants.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-3xl max-h-[80vh] bg-[#0a1628] border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <h2 className="text-white text-lg font-semibold">Manage Registrants</h2>
            <p className="text-gray-400 text-sm mt-0.5">
              {webinarName} — {registrants.length} registrant(s)
              {pendingCount > 0 && (
                <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500/15 text-yellow-400 border border-yellow-500/30">
                  {pendingCount} pending
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportCSV}
              disabled={registrants.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#832CFF]/15 text-[#832CFF] hover:bg-[#832CFF]/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b border-white/5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-[#832CFF]/50"
            />
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto px-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-6 h-6 border-2 border-[#832CFF] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
              <Users className="w-10 h-10 mb-3 opacity-40" />
              <p className="text-sm">{search ? "No matching registrants" : "No registrants yet"}</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 text-xs uppercase tracking-wider border-b border-white/10">
                  <th className="text-left py-3 pr-3 font-medium">#</th>
                  <th className="text-left py-3 pr-3 font-medium">Name</th>
                  <th className="text-left py-3 pr-3 font-medium">Email</th>
                  <th className="text-left py-3 pr-3 font-medium">Registered</th>
                  <th className="text-left py-3 pr-3 font-medium">Status</th>
                  <th className="text-left py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={i} className={`border-b border-white/5 hover:bg-white/[0.02] ${r.status === "pending" ? "bg-yellow-500/[0.03]" : ""}`}>
                    <td className="py-2.5 pr-3 text-gray-500">{r.index}</td>
                    <td className="py-2.5 pr-3 text-white">{r.firstName} {r.lastName}</td>
                    <td className="py-2.5 pr-3 text-gray-300">{r.email}</td>
                    <td className="py-2.5 pr-3 text-gray-400">
                      {r.registeredAt ? new Date(r.registeredAt).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true, timeZoneName: "short" }) : "—"}
                    </td>
                    <td className="py-2.5 pr-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        r.status === "pending"
                          ? "bg-yellow-500/15 text-yellow-400"
                          : "bg-green-500/15 text-green-400"
                      }`}>
                        {r.status === "pending" ? "Pending" : "Approved"}
                      </span>
                    </td>
                    <td className="py-2.5">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => setDetailRegistrant(r)}
                          className="p-1 rounded transition-colors text-gray-400 hover:text-white hover:bg-white/10"
                          title="View full details"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              setActionLoading(r.email);
                              await resendConfirmationEmail(webinarId, r.email);
                              toast.success(`Confirmation email resent to ${r.email}`);
                            } catch (err) {
                              toast.error("Failed to resend email");
                            } finally {
                              setActionLoading(null);
                            }
                          }}
                          disabled={actionLoading === r.email}
                          className="p-1 rounded transition-colors text-gray-400 hover:text-purple-400 hover:bg-white/10 disabled:opacity-40"
                          title="Resend confirmation email"
                        >
                          <Send className="w-3.5 h-3.5" />
                        </button>
                        {approvalMode === "manual" && r.status === "pending" && (
                          <>
                            <button
                              onClick={() => handleApprove(r.email)}
                              disabled={actionLoading === r.email}
                              className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-green-500/15 text-green-400 hover:bg-green-500/25 transition-colors disabled:opacity-40"
                              title="Approve"
                            >
                              <Check className="w-3 h-3" /> Approve
                            </button>
                            <button
                              onClick={() => handleReject(r.email)}
                              disabled={actionLoading === r.email}
                              className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-red-500/15 text-red-400 hover:bg-red-500/25 transition-colors disabled:opacity-40"
                              title="Reject"
                            >
                              <XCircle className="w-3 h-3" /> Reject
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-white/10 flex items-center justify-between">
          <p className="text-gray-500 text-xs">
            Showing {filtered.length} of {registrants.length} registrant(s)
          </p>
        </div>
      </div>

      {/* Registrant Detail Popup */}
      {detailRegistrant && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setDetailRegistrant(null)}>
          <div className="absolute inset-0 bg-black/50" />
          <div
            className="relative w-full max-w-md bg-[#0a1628] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <h3 className="text-white text-sm font-semibold">Registrant Details</h3>
              <button onClick={() => setDetailRegistrant(null)} className="p-1 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3 max-h-[60vh] overflow-auto">
              <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                <div>
                  <span className="text-gray-500 text-xs">First Name</span>
                  <p className="text-white mt-0.5">{detailRegistrant.firstName || "—"}</p>
                </div>
                <div>
                  <span className="text-gray-500 text-xs">Last Name</span>
                  <p className="text-white mt-0.5">{detailRegistrant.lastName || "—"}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500 text-xs">Email</span>
                  <p className="text-white mt-0.5">{detailRegistrant.email || "—"}</p>
                </div>
                {detailRegistrant.phone && (
                  <div>
                    <span className="text-gray-500 text-xs">Phone</span>
                    <p className="text-white mt-0.5">{detailRegistrant.phone}</p>
                  </div>
                )}
                <div>
                  <span className="text-gray-500 text-xs">Registered</span>
                  <p className="text-white mt-0.5">
                    {detailRegistrant.registeredAt
                      ? new Date(detailRegistrant.registeredAt).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
                      : "—"}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500 text-xs">Status</span>
                  <p className="mt-0.5">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      detailRegistrant.status === "pending"
                        ? "bg-yellow-500/15 text-yellow-400"
                        : "bg-green-500/15 text-green-400"
                    }`}>
                      {detailRegistrant.status === "pending" ? "Pending" : "Approved"}
                    </span>
                  </p>
                </div>
              </div>

              {/* Custom Fields */}
              {detailRegistrant.customFields && Object.keys(detailRegistrant.customFields).length > 0 && (
                <>
                  <hr className="border-white/10" />
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wider font-medium mb-2">Custom Fields</p>
                    <div className="grid grid-cols-1 gap-y-2 text-sm">
                      {Object.entries(detailRegistrant.customFields).map(([key, value]) => (
                        <div key={key}>
                          <span className="text-gray-500 text-xs">{key}</span>
                          <p className="text-white mt-0.5">{value || "—"}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main WebinarDashboard ─────────────────────────────────────
export default function WebinarDashboard() {
  const navigate = useNavigate();
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWebinar, setSelectedWebinar] = useState(null);
  const [sendingReplay, setSendingReplay] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("Published");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;

  const fetchWebinars = useCallback(async () => {
    try {
      const res = await listWebinars();
      if (res.success) setWebinars(res.data || []);
    } catch (e) {
      toast.error("Failed to load webinars");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWebinars();
  }, [fetchWebinars]);

  const handleSendReplayEmail = async (webinar) => {
    if (sendingReplay) return;
    if (webinar.registrantCount === 0) {
      toast.warn("No registrants to send email to");
      return;
    }
    setSendingReplay(webinar.id);
    try {
      const res = await sendReplayEmail(webinar.id);
      if (res.success) {
        toast.success(res.message || "Replay email sent!");
      } else {
        toast.error(res.message || "Failed to send");
      }
    } catch (e) {
      toast.error("Failed to send replay email");
    } finally {
      setSendingReplay(null);
    }
  };

  // Filtered + sorted + paginated webinars
  const filteredWebinars = useMemo(() => {
    let list = webinars;
    const now = new Date();

    if (statusFilter === "Published") {
      // Show scheduled and live webinars (not ended, not drafts)
      list = list.filter((w) => {
        if (w.status === "Draft") return false;
        if (w.status === "Ended") return false;
        return true;
      });
    } else if (statusFilter === "Drafts") {
      list = list.filter((w) => w.status === "Draft");
    } else if (statusFilter === "Past") {
      // Only show webinars that the backend marked as Ended
      // (host ended the webinar, or scheduled end time passed for never-started webinars)
      list = list.filter((w) => w.status === "Ended");
    }
    // "All" shows everything

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (w) =>
          (w.roomName || "").toLowerCase().includes(q) ||
          (w.internalName || "").toLowerCase().includes(q)
      );
    }

    // Sort based on active tab
    if (statusFilter === "Past") {
      // Past: most recently ended first (newest → oldest)
      list.sort((a, b) => {
        const dateA = a.scheduledStartTime ? new Date(a.scheduledStartTime).getTime() : 0;
        const dateB = b.scheduledStartTime ? new Date(b.scheduledStartTime).getTime() : 0;
        return dateB - dateA;
      });
    } else if (statusFilter === "All") {
      // All: future webinars first (earliest upcoming), then past (newest first)
      const nowMs = now.getTime();
      list.sort((a, b) => {
        const dateA = a.scheduledStartTime ? new Date(a.scheduledStartTime).getTime() : Infinity;
        const dateB = b.scheduledStartTime ? new Date(b.scheduledStartTime).getTime() : Infinity;
        const aIsFuture = dateA >= nowMs;
        const bIsFuture = dateB >= nowMs;
        if (aIsFuture && !bIsFuture) return -1;
        if (!aIsFuture && bIsFuture) return 1;
        if (aIsFuture && bIsFuture) return dateA - dateB; // earliest upcoming first
        return dateB - dateA; // past: newest first
      });
    } else {
      // Published / Drafts: earliest upcoming first, no-date items at end
      list.sort((a, b) => {
        const dateA = a.scheduledStartTime ? new Date(a.scheduledStartTime).getTime() : Infinity;
        const dateB = b.scheduledStartTime ? new Date(b.scheduledStartTime).getTime() : Infinity;
        return dateA - dateB;
      });
    }

    return list;
  }, [webinars, statusFilter, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredWebinars.length / perPage));
  const paginatedWebinars = filteredWebinars.slice((currentPage - 1) * perPage, currentPage * perPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  const formatDate = (dateString, timezone) => {
    if (!dateString) return "—";
    try {
      return new Intl.DateTimeFormat("en-US", {
        timeZone: timezone || undefined,
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZoneName: "short",
      }).format(new Date(dateString));
    } catch {
      return new Date(dateString).toLocaleDateString();
    }
  };

  return (
    <div className="p-4 md:p-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-white text-2xl font-bold">Webinars</h1>
          <p className="text-gray-400 text-sm mt-1">
            {filteredWebinars.length === webinars.length
              ? `${webinars.length} webinar(s)`
              : `${filteredWebinars.length} of ${webinars.length} webinar(s)`}
          </p>
        </div>
        <button
          onClick={() => navigate("/dashboard/webinar-detail")}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
          style={{ backgroundColor: "#832CFF" }}
        >
          <Plus className="w-4 h-4" /> New Webinar
        </button>
      </div>

      {/* Note about reschedules */}
      <div className="flex items-start gap-3 p-3.5 mb-6 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
        <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 shrink-0" />
        <p className="text-yellow-300/80 text-xs leading-relaxed">
          <strong>Note:</strong> Reminder emails do not update automatically when you reschedule — export the registrant list to notify attendees of any changes.
        </p>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search webinars..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-[#832CFF]/50"
          />
        </div>
        <div className="flex items-center gap-2">
          {["Published", "Drafts", "Past", "All"].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                statusFilter === s
                  ? "bg-[#832CFF] text-white"
                  : "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Webinar List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#832CFF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredWebinars.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-500">
          <Video className="w-14 h-14 mb-4 opacity-30" />
          {webinars.length === 0 ? (
            <>
              <p className="text-lg font-medium text-gray-400 mb-1">No webinars yet</p>
              <p className="text-sm mb-6">Create your first webinar to get started</p>
              <button
                onClick={() => navigate("/dashboard/webinar-detail")}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white"
                style={{ backgroundColor: "#832CFF" }}
              >
                <Plus className="w-4 h-4" /> Create Webinar
              </button>
            </>
          ) : (
            <>
              <p className="text-lg font-medium text-gray-400 mb-1">No matching webinars</p>
              <p className="text-sm">Try adjusting your search or filter</p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {paginatedWebinars.map((w) => (
            <div
              key={w.id}
              className="bg-white/[0.03] border border-white/10 rounded-xl p-4 hover:bg-white/[0.05] transition-colors"
            >
              {/* Top row: title + status */}
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2.5 mb-1">
                    <h3 className="text-white font-semibold text-sm truncate">{w.roomName}</h3>
                    <StatusBadge status={w.status} />
                  </div>
                  {w.internalName && (
                    <p className="text-gray-500 text-xs truncate">{w.internalName}</p>
                  )}
                </div>
                <button
                  onClick={() => navigate(`/dashboard/edit-webinar/${w.id}`)}
                  className="text-gray-400 hover:text-white text-xs flex items-center gap-1 shrink-0"
                >
                  Edit <ChevronRight className="w-3 h-3" />
                </button>
              </div>

              {/* Info row */}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-gray-400 mb-3">
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  {formatDate(w.scheduledStartTime, w.timezone)}
                </span>
                <span className="flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" />
                  {w.registrantCount} registrant{w.registrantCount !== 1 ? "s" : ""}
                </span>
                <span className="flex items-center gap-1.5">
                  <Play className="w-3.5 h-3.5" />
                  Replay: {w.replayEnabled ? (
                    <span className="text-green-400">On</span>
                  ) : (
                    <span className="text-gray-500">Off</span>
                  )}
                </span>
              </div>

              {/* Action row */}
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-white/5">
                {/* Direct join — opens /{roomId} in a new tab. The backend's
                    getRole() recognizes the signed-in user as moderator or
                    co-host and admits them straight into the meeting room
                    without going through the registration page. Hidden once
                    the webinar has Ended; Send Replay Email handles that case. */}
                {w.status !== "Ended" && (
                  <a
                    href={`${window.location.origin}/${w.roomId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#832CFF] text-white hover:bg-[#7322dd] transition-colors"
                  >
                    <Video className="w-3.5 h-3.5" /> Join Now
                  </a>
                )}

                <button
                  onClick={() => setSelectedWebinar(w)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white transition-colors"
                >
                  <Users className="w-3.5 h-3.5" /> Manage Registrants
                </button>

                {w.status === "Ended" && (
                  <button
                    onClick={() => handleSendReplayEmail(w)}
                    disabled={sendingReplay === w.id || w.registrantCount === 0}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#832CFF]/10 text-[#832CFF] hover:bg-[#832CFF]/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {sendingReplay === w.id ? (
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Mail className="w-3.5 h-3.5" />
                    )}
                    Send Replay Email
                  </button>
                )}

                <a
                  href={`${window.location.origin}/webinar/${w.roomId}/event`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white transition-colors ml-auto"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Event Landing Page
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {filteredWebinars.length > perPage && (
        <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/10">
          <p className="text-gray-500 text-xs">
            Showing {(currentPage - 1) * perPage + 1}–{Math.min(currentPage * perPage, filteredWebinars.length)} of {filteredWebinars.length}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-8 h-8 rounded-lg text-xs font-medium transition-colors ${
                  currentPage === page
                    ? "bg-[#832CFF] text-white"
                    : "text-gray-400 hover:bg-white/10 hover:text-white"
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Registrants Modal */}
      {selectedWebinar && (
        <RegistrantsModal
          webinarId={selectedWebinar.id}
          webinarName={selectedWebinar.roomName}
          onClose={() => setSelectedWebinar(null)}
        />
      )}
    </div>
  );
}
